"use client";
/**
 * PALETA DE COMENZI (Ctrl+K, pe Mac Cmd+K) — o singură casetă pentru tot ce face omul des:
 *
 *   „factura”   → ACȚIUNE: deschide direct dialogul de factură nouă, din orice ecran
 *   „stocuri”   → NAVIGARE: sare la pagină, fără să treacă prin rail și panou
 *   „Popescu”   → DATE: clientul, factura, comanda, produsul, găsite pe nume/cod/CUI
 *   „stornare”  → OPERAȚIUNE din registru: consola de operațiuni cu formularul ei deschis
 *
 * Toate secțiunile stau în ACEEAȘI listă, navigabilă cu ↑ ↓ și Enter: mâna nu pleacă de
 * pe tastatură. Acțiunile și navigarea se potrivesc local (sunt deja în pagină, filtrate pe
 * drepturi), datele vin de la server cu 220 ms de așteptare după ultima tastă.
 *
 * ⛔ RBAC: paleta nu decide nimic despre drepturi.
 *   - `actiuni` și `nav` vin gata tăiate de pe server — `nav` e chiar meniul randat, iar
 *     `actiuni` a trecut prin `pageRights` (vezi `lib/nav.ts`). Ce nu e în ele nu există aici.
 *   - operațiunile din registru vin din `paletteActions`, care întoarce doar ce poate rula omul;
 *   - rezultatele de DATE vin din acțiunea `search.global`, care întoarce doar domeniile pe
 *     care omul le poate citi și respectă vizibilitatea pe rând.
 *   - orice intrare e un LINK: dacă cineva ar ajunge unde nu are voie, garda de rută îl oprește
 *     ca înainte. Paleta scurtează drumul, nu poarta.
 *
 * Deschidere: Ctrl+K / Cmd+K, tasta `/` (vezi `scurtaturi.tsx`), sau evenimentul
 * `rodesk-open-palette` — cu `detail.mod = "actiuni"` paleta pornește în regimul „+ Nou”
 * (doar ce se poate crea). Butonul de căutare din rail și câmpul din antet îl emit fără detail.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { I } from "@/components/ui/icons";
import type { NavItem } from "@/types/nav";
import type { QuickAction } from "@/lib/quick-actions";
import { scorPotrivire, PARAM_DESCHIDE } from "@/lib/quick-actions";
import { PARAM_CLIENT, PARAM_CLIENT_NUME } from "@/lib/comanda-url";
import { cautaGlobal } from "@/app/paleta/actions";
import type { PaletteHit } from "@/app/paleta/types";
import { paletteActions, type PaletteAction } from "@/app/actiuni/actions";

/** Evenimentul care deschide paleta de oriunde din aplicație. */
export const EVENIMENT_PALETA = "rodesk-open-palette";
/** Cere paletei să se deschidă; `"actiuni"` = regimul „+ Nou” (doar ce se poate crea). */
export function deschidePaleta(mod?: "actiuni"): void {
  window.dispatchEvent(new CustomEvent(EVENIMENT_PALETA, { detail: mod ? { mod } : {} }));
}

/**
 * PAGINILE DESCHISE DE CURÂND, în browserul omului.
 *
 * În `localStorage`, nu în bază, și nu întâmplător: ar fi însemnat o scriere la fiecare
 * navigare, adică zeci de scrieri pe minut pentru o comoditate. E o preferință a
 * DISPOZITIVULUI — ce am deschis pe laptopul de la birou n-are de ce să apară pe telefon.
 * Ce trebuie să-l urmeze pe om peste dispozitive sunt scurtăturile FIXATE, iar alea stau în
 * bază (`User.navPins`, pe cont).
 *
 * Fiecare acces e învelit în `try`: în fereastră privată sau cu datele de sit blocate,
 * `localStorage` ARUNCĂ la simpla atingere, iar paleta n-are voie să cadă pentru atât.
 */
const CHEIE_RECENTE = "rodesk-paleta-recente";
const CATE_RECENTE = 5;
type Vizita = { href: string; label: string };

function citesteRecente(): Vizita[] {
  try {
    const brut = window.localStorage.getItem(CHEIE_RECENTE);
    const v = brut ? JSON.parse(brut) : [];
    return Array.isArray(v) ? v.filter((x) => x && typeof x.href === "string" && typeof x.label === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Reține pagina deschisă. Chemată din shell, unde se știe și adresa, și eticheta ei din
 * meniul REAL — paleta n-ar avea de unde ști cum se numește o pagină pe care n-a deschis-o ea.
 */
export function tineMinteVizita(href: string, label: string): void {
  try {
    const fara = citesteRecente().filter((v) => v.href !== href);
    window.localStorage.setItem(CHEIE_RECENTE, JSON.stringify([{ href, label }, ...fara].slice(0, 12)));
  } catch {
    /* fereastră privată sau date de sit blocate — se pierde comoditatea, nu funcția */
  }
}

/** Cât se așteaptă după ultima tastă înainte de a întreba serverul. */
const RAGAZ_MS = 220;
/** De la câte caractere are rost să întrebi serverul. */
const MIN_CAUTARE = 2;

type Rand =
  | { tip: "actiune"; id: string; label: string; hint: string; icon: string; href: string }
  | { tip: "navigare"; id: string; label: string; hint: string; icon: string; href: string }
  | { tip: "recent"; id: string; label: string; hint: string; icon: string; href: string }
  | { tip: "date"; id: string; label: string; hint: string; icon: string; href: string; grup: string }
  /* operațiunea din registru: consola de operațiuni cu formularul ei deschis (`/actiuni?a=`) */
  | { tip: "operatiune"; id: string; label: string; hint: string; icon: string; href: string };

/**
 * Acțiunile care se pot porni CU UN CLIENT ÎN BRAȚE. Pornite de pe fișa lui, sosesc cu
 * clientul completat (`?client=`, citit de `useComandaDinUrl` în ecranul-țintă); pornite de
 * altundeva, se comportă ca înainte.
 *
 * Lista e scurtă și explicită dinadins: „Produs nou” pe fișa unui client n-are ce face cu
 * clientul, iar o precompletare care nu are sens e mai rea decât niciuna — omul o observă
 * abia după ce a salvat.
 */
const ACTIUNI_CU_CLIENT = new Set(["factura", "oferta", "comanda", "incasare", "lucrare"]);

/** Clientul de pe ecranul curent, dedus din adresă. `null` = nu suntem pe fișa niciunuia. */
function clientulDinPagina(): { id: string; name: string } | null {
  if (typeof document === "undefined") return null;
  const cale = window.location.pathname;
  const m = /^\/(?:fisa\/Customer|crm\/client)\/([^/?#]+)/.exec(cale);
  if (!m) return null;
  /* Numele se ia din titlul paginii (h1-ul fișei), ca să poată fi ARĂTAT în paletă și în
     formular fără încă o interogare. Dacă nu se găsește, pleacă doar id-ul: câmpul se va
     completa tot corect, doar că fără etichetă până la prima tastare. */
  const titlu = document.querySelector("h1")?.textContent?.trim() ?? "";
  return { id: decodeURIComponent(m[1]), name: titlu };
}

const ETICHETA_DATE: Record<PaletteHit["kind"], { grup: string; icon: string }> = {
  client: { grup: "Clienți", icon: "user" },
  produs: { grup: "Produse", icon: "box" },
  factura: { grup: "Facturi", icon: "receipt" },
  comanda: { grup: "Comenzi", icon: "clipboard" },
  furnizor: { grup: "Furnizori", icon: "truck" },
  proiect: { grup: "Proiecte", icon: "layers" },
};

const FARA_HITS: PaletteHit[] = [];

const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export function CommandPalette({ nav, actiuni }: { nav: NavItem[]; actiuni: QuickAction[] }) {
  const router = useRouter();
  const [deschis, setDeschis] = useState(false);
  const [termen, setTermen] = useState("");
  const [alesBrut, setAles] = useState(0);
  /* Ultimul răspuns de la server, ÎMPREUNĂ cu termenul pentru care a venit: rezultatele se
     arată doar dacă termenul e încă cel din casetă — așa un răspuns vechi sosit peste unul
     nou nu se afișează, iar „se caută…” se deduce (termen ≠ termenul răspuns), nu se ține
     într-o stare scrisă din efect. */
  const [rezultat, setRezultat] = useState<{ termen: string; hits: PaletteHit[] }>({ termen: "", hits: [] });
  const [recente, setRecente] = useState<Vizita[]>([]);
  /* Operațiunile din registru (la care omul are drept) se aduc la PRIMA deschidere, o
     singură dată pe sesiune de pagină; eșecul lasă paleta pe acțiuni + pagini + date. */
  const [operatiuni, setOperatiuni] = useState<PaletteAction[] | null>(null);
  /** „doar acțiuni” = paleta deschisă din butonul „+ Nou”: omul vrea să CREEZE ceva. */
  const [doarActiuni, setDoarActiuni] = useState(false);
  /** Clientul de pe ecranul de unde s-a deschis paleta — citit ÎN CLIPA deschiderii, din
   *  adresa de atunci, nu la montare: paleta trăiește cât ține sesiunea, iar omul se plimbă
   *  prin aplicație sub ea. */
  const [context, setContext] = useState<{ id: string; name: string } | null>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  /* Deschiderea pornește CURAT: termen gol, alegerea pe primul rând, recentele citite ACUM
     (între două deschideri omul a mai umblat prin aplicație). Totul într-un handler, nu într-un
     efect — starea se pune o dată, la eveniment, fără o randare în plus. */
  const deschide = useCallback((mod?: string) => {
    setDoarActiuni(mod === "actiuni");
    setContext(clientulDinPagina());
    setTermen("");
    setAles(0);
    setRecente(citesteRecente());
    setDeschis(true);
  }, []);

  /* ── deschiderea: Ctrl+K (Cmd+K pe Mac) de oriunde, inclusiv dintr-un câmp de text ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setDeschis((d) => { if (d) return false; deschide(); return true; });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deschide]);

  /* Deschiderea din butoane — un eveniment, ca butonul să nu aibă nevoie să stea în același
     arbore de componente cu paleta. Emițătorii vechi (`new Event(...)`, fără detail) rămân valizi. */
  useEffect(() => {
    const onCere = (e: Event) => deschide((e as CustomEvent<{ mod?: string }>).detail?.mod);
    window.addEventListener(EVENIMENT_PALETA, onCere);
    return () => window.removeEventListener(EVENIMENT_PALETA, onCere);
  }, [deschide]);

  useEffect(() => {
    if (!deschis || operatiuni !== null) return;
    let alive = true;
    paletteActions().then((a) => { if (alive) setOperatiuni(a); }).catch(() => { if (alive) setOperatiuni([]); });
    return () => { alive = false; };
  }, [deschis, operatiuni]);

  /* ── datele: se întreabă serverul doar după ce omul s-a oprit din tastat ── */
  const termenCurat = termen.trim();
  const cautaDate = deschis && !doarActiuni && termenCurat.length >= MIN_CAUTARE;
  useEffect(() => {
    if (!cautaDate) return;
    const t = termenCurat;
    const my = ++seq.current;
    const h = setTimeout(() => {
      cautaGlobal(t)
        .then((r) => { if (my === seq.current) setRezultat({ termen: t, hits: r.ok ? r.data : [] }); })
        .catch(() => { if (my === seq.current) setRezultat({ termen: t, hits: [] }); });
    }, RAGAZ_MS);
    return () => clearTimeout(h);
  }, [cautaDate, termenCurat]);
  /* Aceeași listă goală de fiecare dată când nu sunt date: o `[]` nouă la fiecare randare ar
     fi invalidat memo-ul listei degeaba. */
  const hits = cautaDate && rezultat.termen === termenCurat ? rezultat.hits : FARA_HITS;
  const cauta = cautaDate && rezultat.termen !== termenCurat;

  /* ── lista: recente, acțiuni, navigare, date, operațiuni ── */
  const randuri = useMemo<Rand[]>(() => {
    const t = termenCurat;
    const out: Rand[] = [];

    /* Fără niciun termen, paleta nu e o listă de tot ce se poate: e „unde am fost” și „ce fac
       des”. Recentele se filtrează pe meniul CURENT — o pagină rămasă în memoria browserului
       de la altă firmă (alt pachet, alte drepturi) pur și simplu nu apare, fără să fie nevoie
       de vreo cheie de firmă în `localStorage`. */
    if (!t && !doarActiuni) {
      const adreseValide = new Set(
        nav.flatMap((i) => [i.href, ...(i.children ?? []).map((c) => c.href)]).filter((h) => !h.startsWith("#")),
      );
      for (const v of recente.filter((v) => adreseValide.has(v.href)).slice(0, CATE_RECENTE)) {
        out.push({ tip: "recent", id: `r:${v.href}`, label: v.label, hint: "recent", icon: "history", href: v.href });
      }
    }

    const potriviteActiuni = actiuni
      .map((a) => ({ a, s: scorPotrivire(t, a.label, a.keywords) }))
      .filter((x) => x.s > 0)
      .sort((x, y) => y.s - x.s || x.a.label.localeCompare(y.a.label, "ro"))
      .slice(0, doarActiuni ? 20 : t ? 6 : 5);
    for (const { a } of potriviteActiuni) {
      const cuClient = context && ACTIUNI_CU_CLIENT.has(a.key);
      const ctx = cuClient
        ? `&${PARAM_CLIENT}=${encodeURIComponent(context.id)}` +
          (context.name ? `&${PARAM_CLIENT_NUME}=${encodeURIComponent(context.name)}` : "")
        : "";
      out.push({
        tip: "actiune", id: `a:${a.key}`, label: a.label,
        hint: cuClient ? `pentru ${context.name || "clientul curent"}` : "acțiune",
        icon: a.icon ?? "plus",
        href: `${a.href}${a.href.includes("?") ? "&" : "?"}${PARAM_DESCHIDE}=${a.key}${ctx}`,
      });
    }

    if (doarActiuni) return out;

    /* Navigarea se ia din meniul REAL — butoanele de rail cu adresă și toate frunzele lor.
       Grupurile (`#crm`) se sar: n-au pagină proprie. */
    const tinte: { href: string; label: string; parinte?: string; keywords?: string[] }[] = [];
    for (const it of nav) {
      if (!it.href.startsWith("#") && !it.external) tinte.push({ href: it.href, label: it.label, keywords: it.keywords });
      for (const c of it.children ?? []) {
        if (!c.external) tinte.push({ href: c.href, label: c.label, parinte: it.label, keywords: c.keywords });
      }
    }
    const vazute = new Set<string>();
    const potriviteNav = tinte
      .filter((x) => (vazute.has(x.href) ? false : (vazute.add(x.href), true)))
      /* părintele + denumirile alternative (ex. cele din adminul Magento) */
      .map((x) => ({ x, s: scorPotrivire(t, x.label, [...(x.parinte ? [x.parinte] : []), ...(x.keywords ?? [])]) }))
      .filter((y) => y.s > 0)
      .sort((y, z) => z.s - y.s || y.x.label.localeCompare(z.x.label, "ro"))
      .slice(0, 6);
    for (const { x } of potriviteNav) {
      out.push({ tip: "navigare", id: `n:${x.href}`, label: x.label, hint: x.parinte ?? "pagină", icon: "right", href: x.href });
    }

    for (const h of hits) {
      const e = ETICHETA_DATE[h.kind];
      out.push({ tip: "date", id: `d:${h.kind}:${h.id}`, label: h.label, hint: h.hint ?? "", icon: e.icon, href: h.href, grup: e.grup });
    }

    /* Operațiunile din registru vin LA URMĂ și doar cu termen: cine tastează „facturare" vrea
       aproape sigur ecranul sau acțiunea rapidă, nu una dintre cele patruzeci de operațiuni. */
    if (t) {
      const nq = norm(t);
      for (const o of (operatiuni ?? []).filter((o) => norm(o.title).includes(nq) || norm(o.key).includes(nq)).slice(0, 8)) {
        out.push({ tip: "operatiune", id: `o:${o.key}`, label: o.title, hint: o.key, icon: "play", href: `/actiuni?a=${encodeURIComponent(o.key)}` });
      }
    }
    return out;
  }, [termenCurat, actiuni, nav, hits, recente, doarActiuni, operatiuni, context]);

  /* Alegerea e derivată: se readuce pe primul rând la fiecare tastare (în handler), iar când
     lista se scurtează (răspunsul de date a venit cu mai puține rânduri) se strânge la capăt. */
  const ales = Math.max(0, Math.min(alesBrut, randuri.length - 1));

  const alege = useCallback((r: Rand | undefined) => {
    if (!r) return;
    setDeschis(false);
    router.push(r.href);
  }, [router]);

  /* ── tastatura în paletă ── */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); setDeschis(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setAles(Math.min(ales + 1, randuri.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setAles(Math.max(ales - 1, 0)); return; }
    if (e.key === "Home") { e.preventDefault(); setAles(0); return; }
    if (e.key === "End") { e.preventDefault(); setAles(randuri.length - 1); return; }
    if (e.key === "Enter") { e.preventDefault(); alege(randuri[ales]); return; }
  };

  /* Rândul ales rămâne în vizor când lista se derulează cu tastele. */
  useEffect(() => {
    listaRef.current?.querySelector<HTMLElement>('[data-ales="1"]')?.scrollIntoView({ block: "nearest" });
  }, [ales]);

  if (!deschis) return null;

  let grupCurent = "";
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Paletă de comenzi"
    >
      <button aria-label="Închide" tabIndex={-1} onClick={() => setDeschis(false)} className="absolute inset-0 cursor-default bg-ink/25 backdrop-blur-[2px]" />
      <div className="relative flex w-full max-w-[620px] flex-col overflow-hidden rounded-[var(--l-r-pop)] border border-line bg-surface shadow-pop">
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          <span className="text-ink-3"><I name="search" className="!size-4" /></span>
          <input
            autoFocus
            value={termen}
            onChange={(e) => { setTermen(e.target.value); setAles(0); }}
            onKeyDown={onKeyDown}
            placeholder={doarActiuni
              ? (context?.name ? `Ce creezi pentru ${context.name}?` : "Ce vrei să creezi?")
              : "Caută o acțiune, o pagină, un client, un produs, o factură…"}
            aria-label="Caută o acțiune, o pagină sau o înregistrare"
            aria-autocomplete="list"
            className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-3"
          />
          {cauta && <span className="shrink-0 text-[11px] text-ink-3">se caută…</span>}
          <kbd className="shrink-0 rounded border border-line px-1.5 py-0.5 font-mono text-[10px] text-ink-3">ESC</kbd>
        </div>

        <div ref={listaRef} className="max-h-[56vh] overflow-y-auto py-1">
          {randuri.length === 0 && (
            <p className="px-4 py-6 text-center text-[13px] text-ink-3">
              {termenCurat.length < MIN_CAUTARE
                ? "Scrie ce vrei să faci — „factură”, „client nou”, „stocuri” — sau caută un nume, un SKU, un CUI."
                : `Nimic pentru „${termenCurat}”.`}
            </p>
          )}
          {randuri.map((r, i) => {
            const grup = r.tip === "recent" ? "Deschise de curând"
              : r.tip === "actiune" ? "Acțiuni"
              : r.tip === "navigare" ? "Mergi la"
              : r.tip === "operatiune" ? "Operațiuni din registru"
              : r.grup;
            const capNou = grup !== grupCurent;
            grupCurent = grup;
            const ales_ = i === ales;
            return (
              <div key={r.id}>
                {capNou && (
                  <div className="px-4 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-3">{grup}</div>
                )}
                <button
                  type="button"
                  data-ales={ales_ ? "1" : "0"}
                  onMouseEnter={() => setAles(i)}
                  onClick={() => alege(r)}
                  className={
                    "flex w-full items-center gap-3 px-4 py-2 text-left transition " +
                    (ales_ ? "bg-accent-soft" : "hover:bg-surface-2")
                  }
                >
                  <span className={ales_ ? "text-accent" : "text-ink-3"}><I name={r.icon} className="!size-4" /></span>
                  <span className="min-w-0 flex-1 truncate text-[13.5px] text-ink">{r.label}</span>
                  {r.hint && <span className={"shrink-0 truncate text-[11.5px] text-ink-3" + (r.tip === "operatiune" ? " font-mono" : "")}>{r.hint}</span>}
                  {ales_ && <kbd className="shrink-0 rounded border border-line px-1 py-0.5 font-mono text-[10px] text-ink-3">Enter</kbd>}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-[11px] text-ink-3">
          <span>↑ ↓ alegi</span><span>Enter deschizi</span><span>Esc închizi</span>
          <span className="ml-auto font-mono">Ctrl K</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Butonul „+ Nou” din bara de sus: aceleași acțiuni ca în paletă, pentru cine nu ține minte
 * scurtătura. Nu-și face propria listă și nu primește nicio listă — cere paletei să se
 * deschidă în regim de acțiuni, deci filtrarea pe drepturi rămâne într-un singur loc.
 * `ton`: „inchis” pe bara întunecată (Direcții), „deschis” pe antetele de suprafață (Tablou, rail).
 */
export function ButonNou({ ton = "inchis" }: { ton?: "inchis" | "deschis" }) {
  return (
    <button
      type="button"
      onClick={() => deschidePaleta("actiuni")}
      title="Creează ceva nou (Ctrl+K, apoi scrie)"
      className={
        "hidden items-center gap-1.5 rounded-[var(--l-r-ctl)] px-2.5 py-1.5 text-[12.5px] font-semibold transition sm:flex " +
        (ton === "inchis"
          ? "ml-2 bg-white/15 text-rail-ink hover:bg-white/25"
          : "bg-accent text-white shadow-card hover:bg-accent-ink")
      }
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
      </svg>
      Nou
    </button>
  );
}

/** Butonul din bara de sus (Direcții) care deschide paleta — pentru cine nu ține minte scurtătura. */
export function PaletteButton() {
  return (
    <button
      type="button"
      onClick={() => deschidePaleta()}
      title="Caută sau pornește o acțiune (Ctrl+K)"
      className="ml-3 hidden items-center gap-2 rounded-[var(--l-r-ctl)] border border-white/15 bg-white/10 px-2.5 py-1.5 text-[12.5px] text-rail-ink/80 transition hover:bg-white/15 hover:text-rail-ink sm:flex"
    >
      <I name="search" className="!size-3.5" />
      <span>Caută sau pornește…</span>
      <kbd className="rounded border border-white/20 px-1 py-0.5 font-mono text-[10px]">Ctrl K</kbd>
    </button>
  );
}
